import { useState, useEffect } from 'react';
import { api } from '../services/baseUrl';

const BASE_API =
  process.env.REACT_APP_API_BASE_URL || api?.defaults?.baseURL || 'https://api.krayneagency.fr/api';
const API_ROOT = BASE_API.replace(/\/api\/?$/, '/');

// Helper pour construire URL complète de l'image
// IMPORTANT: Toutes les images passent par /api/images/ pour appliquer le watermark
const getImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // Nettoyer le chemin de l'image
  let cleanPath = String(imageData);
  
  // Si c'est déjà une URL complète, extraire le chemin
  if (cleanPath.startsWith('http')) {
    cleanPath = cleanPath.replace(/^https?:\/\/[^\/]+\//, '');
  }
  
  // Retirer les slashes au début
  cleanPath = cleanPath.replace(/^\/+/, '');
  
  // Retirer 'storage/' si présent (déjà dans le chemin du disque)
  if (cleanPath.startsWith('storage/')) {
    cleanPath = cleanPath.substring(8);
  }
  
  // Retirer 'api/images/' si déjà présent pour éviter duplication
  if (cleanPath.startsWith('api/images/')) {
    cleanPath = cleanPath.substring(11);
  }
  
  // Retourner via l'endpoint protégé
  return `${API_ROOT}api/images/${cleanPath}`;
};

export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les catégories
        const categoriesResponse = await api.get('/categories');
        const categoriesRaw = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : (categoriesResponse.data?.data || []);
        if (Array.isArray(categoriesRaw)) {
          setCategories(categoriesRaw);
        }
        
        // Récupérer les produits publics
        const productsResponse = await api.get('/products');
        // Accepter plusieurs formats de réponse: {data: [...]}, [...], {products: [...]}
        const raw = Array.isArray(productsResponse.data)
          ? productsResponse.data
          : (productsResponse.data?.data || productsResponse.data?.products || []);
        if (Array.isArray(raw) && raw.length >= 0) {
          const mappedProducts = raw.map((p) => ({
            id: p.id ?? p.id_product,
            id_product: p.id_product ?? p.id,
            title: p.name ?? p.name_product ?? '',
            name: p.name ?? p.name_product ?? '',
            description: p.description ?? p.description_product ?? '',
            category: p.category ?? p.category_name ?? '',
            price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
            price_ht: p.price ?? p.price_product ?? 0,
            price_ttc: p.price_ttc ?? p.price ?? 0,
            stock: p.available_stock ?? p.stock ?? 0,
            sale_type: p.sale_type ?? 'libre',
            status: p.status ?? 'active',
            image: getImageUrl(p.image ?? p.image_url ?? null),
            creator_type: p.creator_type ?? 'krayne',
            creator_name: p.creator_name ?? 'Krayne Agency',
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    products,
    categories,
    loading,
    error,
  };
};

// Hook spécifique pour le portfolio/creations
// Montre TOUS les produits (libre ET unique) contrairement à useProducts qui filtre pour la boutique
export const usePortfolioProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les catégories
        const categoriesResponse = await api.get('/categories');
        const categoriesRaw = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : (categoriesResponse.data?.data || []);
        if (Array.isArray(categoriesRaw)) {
          setCategories(categoriesRaw);
        }
        
        // Récupérer TOUS les produits actifs pour le portfolio
        const productsResponse = await api.get('/products/portfolio');
        const raw = Array.isArray(productsResponse.data)
          ? productsResponse.data
          : (productsResponse.data?.data || productsResponse.data?.products || []);
        if (Array.isArray(raw)) {
          const mappedProducts = raw.map((p) => ({
            id: p.id ?? p.id_product,
            id_product: p.id_product ?? p.id,
            title: p.name ?? p.name_product ?? '',
            name: p.name ?? p.name_product ?? '',
            description: p.description ?? p.description_product ?? '',
            category: p.category ?? p.category_name ?? '',
            price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
            price_ht: p.price ?? p.price_product ?? 0,
            price_ttc: p.price_ttc ?? p.price ?? 0,
            stock: p.available_stock ?? p.stock ?? 0,
            sale_type: p.sale_type ?? 'libre',
            status: p.status ?? 'active',
            image: getImageUrl(p.image ?? p.image_url ?? null),
            creator_type: p.creator_type ?? 'krayne',
            creator_name: p.creator_name ?? 'Krayne Agency',
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération du portfolio:', err);
        setError('Erreur lors du chargement du portfolio');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    products,
    categories,
    loading,
    error,
  };
};

export const useProductsByCategory = (categoryId) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!categoryId) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/categories/${categoryId}`);
        
        if (response.data?.data?.products) {
          const mappedProducts = response.data.data.products.map((p) => ({
            ...p,
            image: getImageUrl(p.image ?? p.image_url ?? null),
          }));
          setProducts(mappedProducts);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId]);

  return {
    products,
    loading,
    error,
  };
};

export const useProductById = (productId) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/products/${productId}`);
      
      if (response.data?.data) {
        const mappedProduct = {
          ...response.data.data,
          image: getImageUrl(response.data.data.image ?? response.data.data.image_url ?? null),
        };
        setProduct(mappedProduct);
      }
      
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération du produit:', err);
      setError('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  return {
    product,
    loading,
    error,
    refetch: fetchProduct, // Permet de rafraîchir le produit manuellement
  };
};
